import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Sparkles, MessageCircle, CreditCard } from "lucide-react";
import { getServiceBySlug } from "@/data/services";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import LeadDialog from "@/components/landing/LeadDialog";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";

import Services from "@/components/landing/Services";

const WHATSAPP_NUMBER = "919972579453";
const WHATSAPP_URL = (msg) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug);

  const [leadOpen, setLeadOpen] = useState(false);

  const scrollToPricing = () => {
    const el = document.getElementById("service-pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // If service has pricing, the primary CTA scrolls to pricing; else opens lead dialog
  const onPrimaryCTA = service?.pricing ? scrollToPricing : () => setLeadOpen(true);

  if (!service) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 text-slate-200">
        <p className="text-lg">Service not found.</p>
        <Link to="/" className="mt-4 text-blue-400 hover:underline">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-slate-950 text-slate-100" data-testid="service-root">
      <div className="fixed inset-x-0 top-0 z-50">
        <AnnouncementBar onClick={() => setLeadOpen(true)} />
        <Navbar onCTA={() => setLeadOpen(true)} />
      </div>
      <div aria-hidden className="h-[106px]" />

      <main className="mx-auto max-w-5xl px-5 pb-24 pt-10 md:px-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white"
          data-testid="service-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="mt-6 grid items-start gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-blue-300">
              <Sparkles className="h-3.5 w-3.5" /> Service
            </div>
            <h1
              className="mt-4 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl"
              data-testid="service-title"
            >
              {service.title}
            </h1>
            <p className="mt-4 max-w-xl text-base text-slate-300">{service.pitch}</p>
            <p className="mt-2 max-w-xl text-sm text-slate-400">{service.short}</p>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onPrimaryCTA}
                className="btn-primary inline-flex h-12 items-center gap-2 rounded-full px-6 text-sm font-semibold text-white"
                data-testid="service-cta-btn"
              >
                {service.pricing ? "View prices" : "Onboard now"} <ArrowRight className="h-4 w-4" />
              </button>
              {!service.pricing && (
                <a
                  href={WHATSAPP_URL(`Hi Markite! I'm interested in ${service.title}.`)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-12 items-center gap-2 rounded-full bg-emerald-500 px-6 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(16,185,129,0.7)] transition-all hover:-translate-y-0.5 hover:bg-emerald-400"
                  data-testid="service-whatsapp-btn"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              )}
            </div>
          </div>

          <div className="relative lg:col-span-5">
            <div className="absolute -inset-6 -z-10 rounded-full bg-blue-600/20 blur-3xl" aria-hidden />
            <div className="glass overflow-hidden rounded-3xl p-3">
              <img
                src={service.image}
                alt={service.title}
                className="h-56 w-full rounded-2xl object-cover sm:h-64"
                data-testid="service-hero-image"
              />
            </div>
          </div>
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-2">
          <Section title="What's included">
            <ul className="space-y-3">
              {service.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3 text-slate-200">
                  <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-blue-500/15 text-blue-400">
                    <Check className="h-3 w-3" />
                  </span>
                  {d}
                </li>
              ))}
            </ul>
          </Section>
          <Section title="Outcomes you can expect">
            <ul className="space-y-3">
              {service.outcomes.map((d) => (
                <li key={d} className="flex items-start gap-3 text-slate-200">
                  <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
                    <Check className="h-3 w-3" />
                  </span>
                  {d}
                </li>
              ))}
            </ul>
          </Section>
        </div>

        {service.pricing && (
          <div
            id="service-pricing"
            className="mt-14 overflow-hidden rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-slate-950/80 to-blue-950/40 p-8 md:p-12 scroll-mt-32"
            data-testid="service-pricing-block"
          >
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-emerald-300">
                  Pricing
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-extrabold text-white">
                    {service.pricing.total}
                  </span>
                  <span className="text-sm text-slate-400">/ total</span>
                </div>
                <p className="mt-2 text-sm font-semibold text-emerald-300">
                  {service.pricing.note}
                </p>
                <p className="mt-3 text-sm text-slate-300">
                  Start today with just your <span className="font-bold text-white">first installment of {service.pricing.firstInstallment}</span>. The remaining 2 installments are billed across your project milestones — no surprises.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-6 backdrop-blur-md">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                  First installment
                </div>
                <div className="mt-1 font-display text-4xl font-extrabold text-white">
                  {service.pricing.firstInstallment}
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-300">
                  {(service.pricing.bullets || [
                    "Kick-off + discovery",
                    "UI / UX wireframes",
                    "Sprint 1 development",
                  ]).map((b) => (
                    <li key={b} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-400" /> {b}
                    </li>
                  ))}
                </ul>
                <a
                  href={service.pricing.paymentLink}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full text-sm font-semibold text-white"
                  data-testid="service-pay-now-btn"
                >
                  {service.pricing.paymentLabel} <ArrowRight className="h-4 w-4" />
                </a>

                {/* Payment methods strip */}
                <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="payment-methods-strip">
                  <div className="text-center text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                    All payment options available
                  </div>
                  <div className="mt-2 flex items-center justify-center gap-4">
                    <img
                      src="https://cdn.simpleicons.org/phonepe"
                      alt="PhonePe"
                      className="h-5 w-auto"
                      loading="lazy"
                    />
                    <img
                      src="https://cdn.simpleicons.org/googlepay"
                      alt="Google Pay"
                      className="h-5 w-auto"
                      loading="lazy"
                    />
                    <img
                      src="https://cdn.simpleicons.org/paypal"
                      alt="PayPal"
                      className="h-5 w-auto"
                      loading="lazy"
                    />
                    <div className="flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold text-slate-200">
                      <CreditCard className="h-3.5 w-3.5 text-blue-300" />
                      Cards
                    </div>
                  </div>
                </div>

                <p className="mt-3 text-center text-[11px] text-slate-500">
                  Secure payment powered by Razorpay
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-14 overflow-hidden rounded-3xl border border-blue-500/25 bg-gradient-to-br from-blue-950/70 via-slate-950/80 to-indigo-950/70 p-8 md:p-12">
          <div className="text-center">
            <h2 className="font-display text-3xl font-extrabold text-white sm:text-4xl">
              Ready to scale {service.title}?
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-slate-300">
              Trusted by 200+ businesses across India. Transparent milestones, senior engineering talent, and dedicated 24/7 support — every step of the way.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a
                href={WHATSAPP_URL(`Hi Markite! I'd like to talk with an expert about ${service.title}.`)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-12 items-center gap-2 rounded-full bg-emerald-500 px-6 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(16,185,129,0.7)] transition-all hover:-translate-y-0.5 hover:bg-emerald-400"
                data-testid="service-bottom-cta-btn"
              >
                <MessageCircle className="h-4 w-4" /> Talk with expert
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Other suggested services for SEO + cross-discovery */}
      <Services excludeSlug={service.slug} heading="Other services" subheading="Explore more ways we help businesses grow." />

      <Footer />
      <WhatsAppFloat />
      <LeadDialog
        open={leadOpen}
        onOpenChange={setLeadOpen}
        plan={service.title}
        title={`Get started with ${service.title}`}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
      <h3 className="font-display text-xl font-bold text-white">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}
